import { useEffect, useState, type ReactNode } from 'react'
import { Button } from './button'

type Resolver = (value: boolean) => void

type State = {
  title: string
  body?: ReactNode
  confirmLabel: string
  cancelLabel: string
  tone: 'default' | 'danger'
  resolve: Resolver
}

let setStateExternal: ((s: State | null) => void) | null = null

export type ConfirmOptions = {
  title: string
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'default' | 'danger'
}

let pendingResolve: Resolver | null = null

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!setStateExternal) {
      console.warn('ConfirmDialogHost is not mounted yet — auto-resolving false')
      resolve(false)
      return
    }
    if (pendingResolve) {
      pendingResolve(false)
      pendingResolve = null
    }
    pendingResolve = resolve
    setStateExternal({
      title: opts.title,
      body: opts.body,
      confirmLabel: opts.confirmLabel ?? 'Так',
      cancelLabel: opts.cancelLabel ?? 'Скасувати',
      tone: opts.tone ?? 'default',
      resolve: (value) => {
        if (pendingResolve === resolve) pendingResolve = null
        resolve(value)
      },
    })
  })
}

export function ConfirmDialogHost() {
  const [state, setState] = useState<State | null>(null)

  useEffect(() => {
    setStateExternal = setState
    return () => {
      setStateExternal = null
    }
  }, [])

  useEffect(() => {
    if (!state) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        state.resolve(false)
        setState(null)
      } else if (e.key === 'Enter') {
        state.resolve(true)
        setState(null)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [state])

  if (!state) return null

  const close = (result: boolean) => {
    state.resolve(result)
    setState(null)
  }

  return (
    <div
      onClick={() => close(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(420px, 90vw)',
          background: 'var(--bg-1)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{state.title}</h3>
        {state.body && (
          <div style={{ color: 'var(--fg-3)', fontSize: 13 }}>{state.body}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" size="sm" onClick={() => close(false)}>
            {state.cancelLabel}
          </Button>
          <Button
            variant={state.tone === 'danger' ? 'danger' : 'primary'}
            size="sm"
            onClick={() => close(true)}
          >
            {state.confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
